// Canonical client-side model for the sales playbook top-level entity.
// The backend renamed the playbook key field `industry` -> `playbookName`
// (salesguru-backend PR 34); every consumer in this app must read
// `playbookName` only. The single sanctioned legacy read lives in
// readRawPlaybookName below.

export type SalesPlaybookTag =
  | 'situation'
  | 'pain'
  | 'cost_math'
  | 'authority'
  | 'timeline'
  | 'close_trigger';

export interface SalesPlaybookPitchBullet {
  _id: string;
  text: string;
  sortOrder: number;
}

export interface SalesPlaybookQuestion {
  _id: string;
  questionText: string;
  tags: SalesPlaybookTag[];
  sortOrder: number;
  isActive: boolean;
}

export interface SalesPlaybookRole {
  _id: string;
  name: string;
  displayName: string;
  sortOrder: number;
  isActive: boolean;
  pitch: SalesPlaybookPitchBullet[];
  advanced: SalesPlaybookPitchBullet[];
  questions: SalesPlaybookQuestion[];
}

export interface SalesPlaybook {
  _id: string;
  playbookName: string;
  displayName: string;
  roles: SalesPlaybookRole[];
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Light playbook shape returned by the active-playbooks dropdown endpoint. */
export interface SalesPlaybookOption {
  playbookName: string;
  displayName: string;
  roles: Array<{ name: string; displayName: string }>;
}

const toArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/**
 * Resolve the playbook key field from a raw backend object.
 *
 * The `industry` fallback below is the ONLY place this app may read the
 * legacy key. It covers stale caches / in-flight responses while the
 * backend's PLAYBOOK_NAME_LEGACY_READ_FALLBACK window is open.
 * TODO(naren): drop the legacy `industry` read once
 * PLAYBOOK_NAME_LEGACY_READ_FALLBACK is removed backend-side.
 */
const readRawPlaybookName = (raw: Record<string, unknown>): string => {
  const resolvedName = raw?.playbookName ?? raw?.industry ?? '';
  return typeof resolvedName === 'string' ? resolvedName : '';
};

export function normalizePlaybookRole(raw: unknown): SalesPlaybookRole {
  const rawRole = (raw ?? {}) as Record<string, unknown>;
  return {
    _id: typeof rawRole?._id === 'string' ? rawRole._id : '',
    name: typeof rawRole?.name === 'string' ? rawRole.name : '',
    displayName: typeof rawRole?.displayName === 'string' ? rawRole.displayName : '',
    sortOrder: typeof rawRole?.sortOrder === 'number' ? rawRole.sortOrder : 0,
    isActive: typeof rawRole?.isActive === 'boolean' ? rawRole.isActive : true,
    pitch: toArray<SalesPlaybookPitchBullet>(rawRole?.pitch),
    advanced: toArray<SalesPlaybookPitchBullet>(rawRole?.advanced),
    questions: toArray<SalesPlaybookQuestion>(rawRole?.questions),
  };
}

/**
 * Normalize a raw backend playbook into the canonical shape. Tolerates
 * null/undefined/legacy-shaped input so no consumer destructures undefined.
 */
export function normalizePlaybook(raw: unknown): SalesPlaybook {
  const rawPlaybook = (raw ?? {}) as Record<string, unknown>;
  return {
    _id: typeof rawPlaybook?._id === 'string' ? rawPlaybook._id : '',
    playbookName: readRawPlaybookName(rawPlaybook),
    displayName: typeof rawPlaybook?.displayName === 'string' ? rawPlaybook.displayName : '',
    roles: toArray(rawPlaybook?.roles).map(normalizePlaybookRole),
    isActive: typeof rawPlaybook?.isActive === 'boolean' ? rawPlaybook.isActive : false,
    createdAt: typeof rawPlaybook?.createdAt === 'string' ? rawPlaybook.createdAt : undefined,
    updatedAt: typeof rawPlaybook?.updatedAt === 'string' ? rawPlaybook.updatedAt : undefined,
  };
}

/** Normalize one item of the active-playbooks dropdown response. */
export function normalizePlaybookOption(raw: unknown): SalesPlaybookOption {
  const rawOption = (raw ?? {}) as Record<string, unknown>;
  return {
    playbookName: readRawPlaybookName(rawOption),
    displayName: typeof rawOption?.displayName === 'string' ? rawOption.displayName : '',
    roles: toArray<{ name: string; displayName: string }>(rawOption?.roles),
  };
}
